import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import popimg from "../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_n.png";
import CustomConfirmation from '../Common/CustomConfirmation';
import Loading from '../Common/Loading';
import saveIcon from '../Images/save_o.gif';
import addIcon from '../Images/add_d.gif';
import editIcon from '../Images/edit_o.gif';
import deleteIcon from '../Images/delete_d.gif';
import cancelIcon from '../Images/cancel_o.gif';


const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

interface FamilyData {
  MDs_Available: string;
  SendKGReport: boolean;
  SendBrickReport: boolean;
  SendWPReport: boolean;
  CanAccessLessonPlan: boolean;
  CanEarnPoints: boolean;
  EligibleForIncentives: boolean;
  GetsDefaultLessonPlan_English: boolean;
  GetsDefaultLessonPlan: boolean;
  ShowScience: boolean;
  EligibleForComps: boolean;
}

interface StudentData {
  SchoolCode: string;
  FamilyCode: string;
  Login: string;
  FName: string;
  Std: string;
  CanAccessLessonPlan: string;
  CanEarnPoints: string;
  WLP_Competency_English: string;
  WLP_Competency_Maths: string;
  Curriculum: string;
  Streams: string;
}

// ── Types from EL_AddEditTask ──────────────────────────────────────────────
interface TaskCustomer {
  surname: string;
  parentsName: string;
  address: string;
  suburb: string;
  postcode: string;
  state: string;
  phone: string;
  mobile1: string;
  mobile2: string;
  email: string;
  dateOfSale: string;
  packages: string;
  distributor: string;
  paymentDue: boolean;
  localTime?: string;
  lastAccessOn?: string;
}

interface TaskStudent {
  id: string;
  firstName: string;
  gender: string;
  grade: string;
  learningDifficulty: string | null;
  isLoggedIn: boolean;
  isSelective: boolean;
}

interface Task {
  problemId: number;
  description: string;
  from: string;
  to: string;
  enteredDate: string;
  actionDate: string;
  isFixed: boolean;
  type: string;
  reason: string;
  outcome: string;
  timeSpent: string;
  hasAttachment: boolean;
}

interface TaskPageData {
  customer: TaskCustomer;
  students: TaskStudent[];
  tasks: Task[];
}

// ──────────────────────────────────────────────────────────────────────────

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
};

const EL_EditFamilyProfile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [familyData, setFamilyData] = useState<FamilyData>({
    MDs_Available: "",
    SendKGReport: false,
    SendBrickReport: false,
    SendWPReport: false,
    CanAccessLessonPlan: false,
    CanEarnPoints: false,
    EligibleForIncentives: false,
    GetsDefaultLessonPlan_English: false,
    GetsDefaultLessonPlan: false,
    ShowScience: false,
    EligibleForComps: false
  });
  const [studentData, setStudentData] = useState<StudentData[]>([]);
  const [curriculumBased, setCurriculumBased] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [selectedRow, setSelectedRow] = useState<StudentData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // ── Task-context state ───────────────────────────────────────────────────
  const [taskData, setTaskData] = useState<TaskPageData | null>(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState("");
  const [loding, setLoading] = useState(true);
  // ────────────────────────────────────────────────────────────────────────

  const schoolCode = searchParams.get("SchoolCode") || searchParams.get("schoolCode") || "";
  const familyCode = searchParams.get("FamilyCode") || searchParams.get("familyCode") || "";
  console.log("EL_EditFamilyProfile: schoolCode =", schoolCode, ", familyCode =", familyCode);
  const lParam = searchParams.get("Login") || "";
  console.log("EL_EditFamilyProfile: lParam =", lParam);
  const prevParam = searchParams.get("Prev") || "";

  const isFromAddEditTask = prevParam.includes("EL_AddEditTask");

  // ── Fetch family-profile data ────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}api/get-family-profile/?SchoolCode=${schoolCode}&FamilyCode=${lParam}`
      );
      const data = await response.json();

      if (data.error) { setErrorMessage(data.error); setLoading(false); return; }

      setFamilyData(data.family_data);
      setStudentData(data.student_data);
      setCurriculumBased(data.curriculum_based);
      setCanAdd(data.can_add);
      setLoading(false);
    } catch (error) {
      setErrorMessage("Error loading data. Please try again.");
      console.error(error);
      setLoading(false);
    }
  };

  // ── Fetch task / support data (only when coming from EL_AddEditTask) ─────
  const fetchTaskData = async () => {
    if (!isFromAddEditTask) return;
    setTaskLoading(true);
    setTaskError("");
    try {
      const params = new URLSearchParams({
        SchoolCode: schoolCode,
        FamilyCode: familyCode,
        Login: lParam,
        S: schoolCode,
        F: familyCode,
        L: lParam
      });

      const response = await fetch(
        `${API_BASE}api/get_support_task_data/?${params.toString()}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Normalise to the same shape used in EL_AddEditTask
      const rawCust = result.customer || {};
      const flags = result.flags || {};

      const customer: TaskCustomer = {
        surname: rawCust.surname || '',
        parentsName: rawCust.parents_name || '',
        address: rawCust.address || '',
        suburb: rawCust.suburb || '',
        postcode: rawCust.postcode || '',
        state: rawCust.state || '',
        phone: rawCust.phone || '',
        mobile1: rawCust.mobile1 || '',
        mobile2: rawCust.mobile2 || '',
        email: rawCust.email || '',
        dateOfSale: rawCust.date_of_sale || '',
        packages: rawCust.packages || '',
        distributor: rawCust.distributor || '',
        paymentDue: flags.payment_due || rawCust.paymentDue || false,
        localTime: rawCust.localTime || '',
        lastAccessOn: rawCust.lastAccessOn || ''
      };

      const students: TaskStudent[] = (result.students || []).map((s: any) => ({
        id: s.id || '',
        firstName: s.first_name || '',
        gender: s.gender || '',
        grade: s.grade || '',
        learningDifficulty: s.learning_difficulty || null,
        isLoggedIn: s.is_logged_in || false,
        isSelective: s.is_selective || false
      }));

      const tasks: Task[] = (result.history || []).map((t: any) => ({
        problemId: t.ProblemID,
        description: t.Description,
        from: t.FromLogin,
        to: t.ToLogin,
        enteredDate: t.EnteredDate,
        actionDate: t.ActionDate,
        isFixed: t.Fixed === true,
        type: t.TypeOfIssue,
        reason: t.Reason,
        outcome: t.CallOutcome,
        timeSpent: t.TimeSpent,
        hasAttachment: (t.AttachCount && t.AttachCount > 0) || false
      }));

      setTaskData({ customer, students, tasks });
    } catch (err: any) {
      setTaskError(err.message || "Error loading support data.");
      console.error(err);
    } finally {
      setTaskLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTaskData();
  }, [schoolCode, familyCode]);

  useEffect(() => {
    if (successMessage) { alert(successMessage); setSuccessMessage(""); }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) { alert(errorMessage); setErrorMessage(""); }
  }, [errorMessage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, type, checked, value } = e.target;
    if (type === "checkbox") {
      setFamilyData({ ...familyData, [id]: checked });
    } else {
      if (/^\d*$/.test(value) || value === "") {
        setFamilyData({ ...familyData, [id]: value });
      }
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_BASE}api/save-family-profile/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...familyData, SchoolCode: schoolCode, FamilyCode: familyCode })
      });
      const data = await response.json();
      if (data.error) { setErrorMessage(data.error); }
      else { setSuccessMessage("Profile saved successfully"); fetchData(); }
    } catch (error) {
      setErrorMessage("Error saving data. Please try again.");
      console.error(error);
    }
  };

  const openCenteredWindow = (url: string) => {
    const width = 600;
    const height = 650;

    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      url,
      "AddEditFamilyUser",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  const handleAddChild = () => {
    openCenteredWindow(
      `/DEALER/EL_AddEditFamilyUser?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${lParam}`
    );
  };

  const handleEditChild = () => {
    if (!selectedRow) {
      alert("Please select a child");
      return;
    }

    openCenteredWindow(
      `/DEALER/EL_AddEditFamilyUser?SchoolCode=${selectedRow.SchoolCode}&FamilyCode=${familyCode}&Login=${lParam}&ChildLogin=${selectedRow.Login}`
    );
  };

  const handleDeleteChild = () => {
    if (!selectedRow) { alert("Please select a child"); return; }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}api/delete-child2/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          SchoolCode: selectedRow?.SchoolCode,
          FamilyCode: selectedRow?.FamilyCode,
          Login: selectedRow?.Login
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Delete failed with status ${response.status}`);
      }
      if (data.error) {
        throw new Error(data.error);
      }
      setSelectedRow(null);
      await fetchData();
    } catch (error: any) {
      setErrorMessage(error.message || "Error deleting child. Please try again.");
      setLoading(false);
      console.error(error);
    }
  };

  // const handleCancel = () => {
  //   if (isFromAddEditTask) {
  //     window.close();          // opened as popup from EL_AddEditTask
  //   } else {
  //     navigate(`/DEALER/EL_ListFamilies?SchoolCode=${schoolCode}`);
  //   }
  // };
  const handleCancel = () => {
    window.close();
  };

  const formatCompetency = (value: string) => {
    const competencies: { [key: string]: string } = {
      "1": "Remedial",
      "2": "Intermediate",
      "3": "Intermediate(High)",
      "4": "Advanced",
      "5": "Extension"
    };
    return competencies[value] || value;
  };

  const getImageProps = (src: string) => ({
    src,
    onMouseOver: (e: React.MouseEvent<HTMLImageElement>) => e.currentTarget.src = src,
    onMouseOut: (e: React.MouseEvent<HTMLImageElement>) => e.currentTarget.src = src,
    onMouseDown: (e: React.MouseEvent<HTMLImageElement>) => e.currentTarget.src = src,
    style: { cursor: "pointer" }
  });

  // ── Shared cell style for task history table ─────────────────────────────
  const taskTd: React.CSSProperties = {
    padding: "4px 6px",
    border: "1px solid #80A0FF",
    fontSize: "11px",
    fontFamily: "Arial",
    verticalAlign: "top"
  };

  // ── Styles for Add/Edit Student Details table ────────────────────────────
  const thStyle: React.CSSProperties = {
    padding: "5px 6px",
    border: "1px solid #80A0FF",
    borderTop: "none",
    textAlign: "left",
    fontWeight: "normal",
    fontSize: "12px",
    verticalAlign: "bottom",
    whiteSpace: "nowrap"
  };

  const tdStyle: React.CSSProperties = {
    padding: "8px 6px",
    borderRight: "1px solid rgb(128, 160, 255)",
    fontSize: "12px",
    fontFamily: "Arial",
    verticalAlign: "middle"
  };

  return (
    <>
      {/* ── Header bar ──────────────────────────────────────────────────── */}
      <div
        style={{
          backgroundImage: `url(${popimg}), url(${popbg})`,
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundPosition: "left center, right center",
          backgroundSize: "30% 100%, 70% 100%",
          height: "16px",
          color: "white",
          padding: "7px 0px",
          fontWeight: "bold",
          fontSize: "12pt",
          fontFamily: "arial",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <span style={{ marginLeft: "10px" }}>Edit Family Profile</span>
        {!isFromAddEditTask && (
          <img
            src={popclose}
            alt="close"
            height="30px"
            style={{ cursor: "pointer" }}
            onClick={() => window.close()}
          />
        )}
      </div>
      {loding ? (
        <Loading />
      ) : (
        <div style={{ backgroundColor: "azure", margin: 0, minHeight: "100vh" }}>

          {/* ── Page title ────────────────────────────────────────────────── */}
          <div style={{ textAlign: "center" }}>
            <table style={{ width: "95%", margin: "0 auto", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{
                    backgroundColor: "teal", textAlign: "center", color: "white",
                    fontSize: "14pt", fontFamily: "Arial", fontWeight: "bold", padding: "5px"
                  }}>
                    <b>Edit Family Profile</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Family-profile form ───────────────────────────────────────── */}
          <div style={{ textAlign: "center" }}>
            <table style={{
              width: "95%", margin: "0 auto", padding: "5px",
              fontFamily: "Arial", fontSize: "11pt",
              border: "1px solid black", borderCollapse: "collapse"
            }}>
              <tbody>
                {/* Maths Doctor Licences */}
                <tr>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    Maths Doctor Licences
                    <input
                      type="text"
                      id="MDs_Available"
                      value={familyData.MDs_Available === "" ? 0 : familyData.MDs_Available}
                      onChange={handleInputChange}
                      style={{
                        width: "30px",
                        border: "1px solid #999",
                        padding: "2px",
                        fontFamily: "Arial",
                        fontSize: "11pt",
                        backgroundColor: "white"
                      }}
                      maxLength={2}
                      onKeyPress={(e) => {
                        if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace') e.preventDefault();
                      }}
                    />
                  </td>
                </tr>

                {/* Checkbox rows */}
                <tr>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="SendBrickReport" checked={familyData.SendBrickReport}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="SendBrickReport" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Brick Wall Report
                    </label>&nbsp;&nbsp;
                  </td>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="EligibleForIncentives" checked={familyData.EligibleForIncentives}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="EligibleForIncentives" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Eligible For Incentives
                    </label>
                  </td>
                </tr>

                <tr>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="SendKGReport" checked={familyData.SendKGReport}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="SendKGReport" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Knowledge Gaps Report
                    </label>&nbsp;&nbsp;
                  </td>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="CanAccessLessonPlan" checked={familyData.CanAccessLessonPlan}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="CanAccessLessonPlan" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Can Access Lesson Plan
                    </label>
                  </td>
                </tr>

                <tr>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="SendWPReport" checked={familyData.SendWPReport}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="SendWPReport" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Study Plan Report
                    </label>
                  </td>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="CanEarnPoints" checked={familyData.CanEarnPoints}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="CanEarnPoints" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Can Earn Points
                    </label>
                  </td>
                </tr>

                <tr>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="GetsDefaultLessonPlan" checked={familyData.GetsDefaultLessonPlan}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="GetsDefaultLessonPlan" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Get Default Lesson Plan - Maths
                    </label>
                  </td>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="GetsDefaultLessonPlan_English" checked={familyData.GetsDefaultLessonPlan_English}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="GetsDefaultLessonPlan_English" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Get Default Lesson Plan - English
                    </label>
                  </td>
                </tr>

                <tr>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="ShowScience" checked={familyData.ShowScience}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="ShowScience" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Show Science Subject
                    </label>
                  </td>
                  <td style={{ textAlign: "left", padding: "5px" }}>
                    <input type="checkbox" id="EligibleForComps" checked={familyData.EligibleForComps}
                      onChange={handleInputChange} style={{ marginRight: "5px", verticalAlign: "middle" }} />
                    <label htmlFor="EligibleForComps" style={{ fontFamily: "Arial", fontSize: "11pt", verticalAlign: "middle" }}>
                      Eligible For Competitions
                    </label>
                  </td>
                </tr>

                {/* Save button */}
                <tr>
                  <td colSpan={2} style={{ textAlign: "center", height: "40px" }}>
                    <img {...getImageProps(saveIcon)} alt="Save" onClick={handleSave} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <br />

          {/* ── Children table  (Image-1 layout) ─────────────────────────── */}
          <div style={{ textAlign: "center" }}>
            <table style={{
              width: "95%", margin: "0 auto",
              border: "1px solid #80A0FF", borderCollapse: "collapse",
              fontFamily: "Arial", fontSize: "11pt"
            }}>
              <tbody>
                {/* Title row */}
                <tr>
                  <td colSpan={9} style={{
                    backgroundColor: "#c7ddfd", color: "#2859ac",
                    textAlign: "center", fontWeight: "bold",
                    fontSize: "12pt", padding: "6px",
                    border: "1px solid #80A0FF"
                  }}>
                    Add/Edit Student Details
                  </td>
                </tr>

                {/* Table + scroll area */}
                <tr>
                  <td colSpan={9} style={{ padding: "0" }}>
                    <div style={{ maxHeight: "230px", overflowY: "auto", overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial", fontSize: "11pt" }}>
                        <thead>
                          {/* Row 1 – base columns + merged "Competency" */}
                          <tr style={{ backgroundColor: "#c7ddfd", color: "#2859ac" }}>
                            <th rowSpan={2} style={thStyle}>#</th>
                            <th rowSpan={2} style={thStyle}>Student<br />Name</th>
                            <th rowSpan={2} style={thStyle}>Grade<br />Year</th>
                            <th rowSpan={2} style={thStyle}>Can Access<br />LP</th>
                            <th rowSpan={2} style={thStyle}>Can Earn<br />Points</th>
                            <th colSpan={3} style={{ ...thStyle, textAlign: "center" }}>Competency</th>
                            <th rowSpan={2} style={thStyle}>Subject/<br />Streams</th>
                          </tr>
                          {/* Row 2 – Competency sub-columns */}
                          <tr style={{ backgroundColor: "#c7ddfd", color: "#2859ac" }}>
                            <th style={thStyle}>English</th>
                            <th style={thStyle}>Maths</th>
                            <th style={thStyle}>Curriculu<wbr />m</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentData.length === 0 ? (
                            <tr>
                              <td colSpan={9} style={{ textAlign: "center", padding: "10px", color: "#555" }}>
                                No students found
                              </td>
                            </tr>
                          ) : (
                            studentData.map((row, index) => (
                              <tr
                                key={index}
                                onClick={() => setSelectedRow(row)}
                                style={{
                                  backgroundColor: selectedRow?.Login === row.Login ? "orange" : index % 2 === 0 ? "white" : "#f4faff",
                                  cursor: "pointer", textAlign: "left",
                                  borderBottom: "none"
                                }}
                              >
                                <td style={tdStyle}>{index + 1}</td>
                                <td style={tdStyle}>{row.FName}</td>
                                <td style={tdStyle}>{row.Std}</td>
                                <td style={tdStyle}>{row.CanAccessLessonPlan}</td>
                                <td style={tdStyle}>{row.CanEarnPoints}</td>
                                <td style={tdStyle}>{formatCompetency(row.WLP_Competency_English)}</td>
                                <td style={tdStyle}>{formatCompetency(row.WLP_Competency_Maths)}</td>
                                <td style={tdStyle}>{row.Curriculum}</td>
                                <td style={tdStyle}>{row.Streams}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>

                {/* Action buttons row */}
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: "6px", border: "1px solid #80A0FF" }}>
                    <img {...getImageProps(addIcon)} alt="Add" onClick={handleAddChild} style={{ cursor: "pointer", margin: "0 10px" }} />
                    <img {...getImageProps(editIcon)} alt="Edit" onClick={handleEditChild} style={{ cursor: "pointer", margin: "0 10px" }} />
                    <img {...getImageProps(deleteIcon)} alt="Delete" onClick={handleDeleteChild} style={{ cursor: "pointer", margin: "0 10px" }} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <br />

          {/* ── Cancel / Close button ────────────────────────────────────── */}
          <div style={{ width: "95%", textAlign: "center", margin: "0 auto" }}>
            <table style={{ borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td>
                    <img {...getImageProps(cancelIcon)} alt="Cancel" onClick={handleCancel} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Delete confirmation dialog ────────────────────────────────── */}
          {showDeleteConfirm && (
            <CustomConfirmation
              message={`Do you want to delete ${selectedRow?.Login}?`}
              onConfirm={confirmDelete}
              onClose={() => setShowDeleteConfirm(false)}
            />
          )}
        </div>
      )}
    </>
  );
};

export default EL_EditFamilyProfile;
